// Shows a 3D model from the project in the Files pane's preview, drawn by
// Blitz3D+ itself: the model viewer is a Blitz program
// (tools/model_viewer/ModelViewer.bb), which the site holds compiled
// (tools/build_model_viewer.mjs). It runs in a frame of its own, beside the
// program's, so a preview never stops a running program, and in a
// transparent one, so the pane shows around the model.
//
// The viewer reports the model (its triangles, size and animation clips) as
// a page message once it shows, and the frame stays invisible until then, so
// neither the run page's own colour nor a half-made first frame flashes in
// the pane. The clip list, Wireframe and Reset view below the frame send it
// messages back; W in the frame switches wireframe too.

import { Runner } from "./runner.js";
import { versioned } from "./site.js";

// The kinds of model LoadAnimMesh reads.
export const MODEL = /\.(glb|gltf|b3d|x|3ds)$/i;

const element = (tag, className, text) => {
  const made = document.createElement(tag);
  if (className) made.className = className;
  if (text !== undefined) made.textContent = text;
  return made;
};

// The frame draws at the pane's size in device pixels, up to this many
// across, and starts again at the new size once the pane stops changing.
const MOST_PIXELS = 2048, RESIZE_DELAY = 400;

export class ModelViewer {
  // base: the viewer's folder on the site; runPath and runOriginFor as for
  // the program's Runner.
  constructor({ base, runPath, runOriginFor }) {
    this.base = base;
    this.runPath = runPath;
    this.runOriginFor = runOriginFor;
    this.wasm = null;
    this.runner = null;
    this.shown = null;
    this.resizing = null;
    this.timer = null;
  }

  // The viewer's program, fetched once.
  load() {
    this.wasm ??= fetch(versioned(new URL("program.wasm", this.base))).then((response) => {
      if (!response.ok) throw new Error(`The model viewer is missing (${response.status})`);
      return response.arrayBuffer();
    }).catch((error) => {
      this.wasm = null;
      throw error;
    });
    return this.wasm;
  }

  // Shows the model at path in the project (whose files assets() packs, as
  // runner.js's packAssets does) in container, which this fills.
  show(container, path, assets) {
    this.stop();
    const parts = {
      view: element("div", "model-view"),
      info: element("div", "model-info"),
      status: element("span", "model-status", "Loading the model…"),
    };
    parts.info.append(parts.status);
    const hint = element("p", "muted model-hint", "Drag to turn the model; use the wheel to zoom; W shows the wireframe.");
    container.classList.add("showing-model");
    container.append(parts.view, parts.info, hint);
    // The clip and wireframe chosen, kept when the frame starts again at a
    // new size.
    const shown = this.shown = { container, path, assets, parts, hint, size: null, clip: 0, wireframe: false };
    this.resizing = new ResizeObserver(() => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (this.shown !== shown) return;
        const size = this.size(parts.view);
        const [width, height] = shown.size || [0, 0];
        // Small changes keep the frame, scaled to fit.
        if (Math.abs(size[0] - width) > width * 0.15 || Math.abs(size[1] - height) > height * 0.15) this.start(shown);
      }, RESIZE_DELAY);
    });
    this.resizing.observe(parts.view);
    this.start(shown);
  }

  // The size to draw at for the view: its size in device pixels.
  size(view) {
    const ratio = Math.min(devicePixelRatio || 1, 2);
    let width = Math.max(64, Math.round(view.clientWidth * ratio));
    let height = Math.max(64, Math.round(view.clientHeight * ratio));
    const scale = Math.min(1, MOST_PIXELS / Math.max(width, height));
    return [Math.round(width * scale), Math.round(height * scale)];
  }

  async start(shown) {
    const { parts, path } = shown;
    let wasm, assets;
    try {
      [wasm, assets] = await Promise.all([this.load(), shown.assets()]);
    } catch (error) {
      if (this.shown === shown) parts.status.textContent = String(error && error.message || error);
      return;
    }
    if (this.shown !== shown) return;
    parts.view.classList.remove("ready");
    const size = shown.size = this.size(parts.view);
    this.runner ??= new Runner({
      pane: parts.view, runPath: this.runPath, runOriginFor: this.runOriginFor, pinned: true, transparent: true,
      events: {
        started() {}, output() {}, firstFrame() {}, responding() {},
        ended: (status, message) => {
          if (this.shown && status) this.failed(message || "The model viewer stopped.");
        },
        pageMessage: (message) => this.heard(message),
      },
    });
    this.runner.pane = parts.view;
    const slash = path.lastIndexOf("/");
    this.runner.run({
      title: "Model viewer",
      debug: false,
      wasm: wasm.slice(0),
      assets,
      launchDirectory: slash < 0 ? "." : path.slice(0, slash),
      tool: { commandLine: `"/app/${path}" ${size[0]} ${size[1]}`, assets: [], environment: {} },
      focus: false,
      openUrls: false,
    });
  }

  // What the viewer says about the model.
  heard(message) {
    const shown = this.shown;
    if (!shown || !message || typeof message !== "object") return;
    if (message.type === "failed") {
      this.failed(message.message);
    } else if (message.type === "model") {
      this.describe(shown, message);
      // A frame started again at a new size gets the choices back.
      if (shown.clip !== 0) this.runner.pageMessage({ type: "clip", index: shown.clip });
      if (shown.wireframe) this.runner.pageMessage({ type: "wireframe", on: true });
      shown.parts.view.classList.add("ready");
    } else if (message.type === "wireframe") {
      shown.wireframe = !!message.on;
      if (shown.parts.wireframe) shown.parts.wireframe.checked = shown.wireframe;
    }
  }

  failed(message) {
    const { parts, hint } = this.shown;
    this.runner.stop();
    hint.remove();
    parts.view.replaceChildren(element("p", "model-failed", `The model could not be shown: ${message}`));
    parts.info.replaceChildren();
  }

  describe(shown, model) {
    const { parts } = shown;
    const number = (value) => Number(value).toLocaleString();
    const length = (value) => Number(value).toPrecision(3).replace(/\.?0+$/, "");
    const facts = [`${number(model.triangles)} triangles`, `${number(model.vertices)} vertices`];
    if (Array.isArray(model.size)) facts.push(`${model.size.map(length).join(" × ")} units`);
    parts.info.replaceChildren(element("span", "model-status", facts.join(", ")));
    const clips = Array.isArray(model.clips) ? model.clips : [];
    if (clips.length) {
      const label = element("label", "", "Animation ");
      const choice = element("select");
      choice.append(new Option("None", "-1"));
      clips.forEach((name, index) => choice.append(new Option(name || `Clip ${index + 1}`, String(index))));
      choice.value = String(shown.clip);
      choice.addEventListener("change", () => {
        shown.clip = Number(choice.value);
        this.runner.pageMessage({ type: "clip", index: shown.clip });
      });
      label.append(choice);
      parts.info.append(label);
    }
    const wireframe = element("label", "", "Wireframe ");
    const box = parts.wireframe = element("input");
    box.type = "checkbox";
    box.checked = shown.wireframe;
    box.title = "Show the triangles' edges (W)";
    box.addEventListener("change", () => {
      shown.wireframe = box.checked;
      this.runner.pageMessage({ type: "wireframe", on: shown.wireframe });
    });
    wireframe.prepend(box);
    parts.info.append(wireframe);
    const reset = element("button", "", "Reset view");
    reset.type = "button";
    reset.addEventListener("click", () => this.runner.pageMessage({ type: "reset" }));
    parts.info.append(reset);
  }

  // Ends the viewer, as another file is shown or the preview closes.
  stop() {
    clearTimeout(this.timer);
    if (this.resizing) this.resizing.disconnect();
    this.resizing = null;
    if (this.runner) this.runner.stop();
    if (this.shown) this.shown.container.classList.remove("showing-model");
    this.shown = null;
  }
}
